import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { renderMarkdown } from './markdown.js';
import { getHelp, type HelpKey } from './registry.js';

interface HelpHintProps {
  helpKey: HelpKey;
  /** Optional className to layout the trigger button (e.g. extra margin). */
  className?: string;
}

/**
 * Inline "?" icon that opens a popover with the help entry's summary.
 * Click to toggle. Esc closes. Outside-click closes.
 */
export function HelpHint({ helpKey, className }: HelpHintProps) {
  const entry = getHelp(helpKey);
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const popoverId = useId();
  const [position, setPosition] = useState<'right' | 'below'>('right');

  // Position the popover to the right of the icon by default; flip below
  // if "right" would overflow the viewport.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const popoverWidth = 280;
    if (rect.right + popoverWidth + 12 > window.innerWidth) {
      setPosition('below');
    } else {
      setPosition('right');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    function onClickOutside(ev: MouseEvent) {
      const target = ev.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [open]);

  const summarySnippet = entry.summary.slice(0, 40);
  const ariaLabel = `Help: ${summarySnippet}${entry.summary.length > 40 ? '…' : ''}`;

  return (
    <span className={`relative inline-flex align-middle ${className ?? ''}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          setShowDetails(false);
        }}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        data-testid={`help-hint-${helpKey}`}
        className="inline-flex h-3 w-3 items-center justify-center rounded-full border border-[color:var(--color-muted-foreground)] text-[9px] leading-none text-[color:var(--color-muted-foreground)] transition-colors hover:border-[color:var(--color-primary)] hover:text-[color:var(--color-primary)] focus:outline-none focus:ring-1 focus:ring-[color:var(--color-primary)]"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 12 12"
          width={10}
          height={10}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        >
          <path d="M4.25 4.25c0-.97.78-1.75 1.75-1.75s1.75.78 1.75 1.75-1.75 1-1.75 2.5" />
          <circle cx="6" cy="9" r="0.5" fill="currentColor" stroke="none" />
        </svg>
      </button>

      {open && (
        <div
          ref={popoverRef}
          id={popoverId}
          role="dialog"
          aria-label="Help"
          data-testid={`help-popover-${helpKey}`}
          className={
            position === 'right'
              ? 'absolute left-full top-0 z-50 ml-2 w-[280px] rounded border border-[color:var(--color-border)] bg-card p-3 shadow-lg'
              : 'absolute left-0 top-full z-50 mt-2 w-[280px] rounded border border-[color:var(--color-border)] bg-card p-3 shadow-lg'
          }
        >
          <p className="text-xs leading-relaxed text-[color:var(--color-fg)]">{entry.summary}</p>
          {entry.details ? (
            <div className="mt-2">
              {showDetails ? (
                <div className="space-y-2 border-t border-[color:var(--color-border)] pt-2">
                  {renderMarkdown(entry.details)}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowDetails(true)}
                  className="text-[10px] font-medium text-[color:var(--color-primary)] hover:underline"
                  data-testid={`help-show-more-${helpKey}`}
                >
                  Show more
                </button>
              )}
            </div>
          ) : null}
          {entry.related && entry.related.length > 0 ? (
            <p className="mt-2 border-t border-[color:var(--color-border)] pt-2 text-[10px] text-[color:var(--color-muted-foreground)]">
              Related: {entry.related.join(', ')}
            </p>
          ) : null}
        </div>
      )}
    </span>
  );
}
